/**
 * @file うんどうタブ — 運動領域のホーム。
 *
 * @what: うんどうチェック (本の遊び × ◎○△×) と、週メニューの 4 ページをこの画面の中に
 *   埋め込み表示する (タップで全画面拡大、本だなで開く小リンクつき)。どちらも本だなに
 *   資料があるときだけ出す。
 * @why: 領域 (学習/うんどう/おんがく…) を第一の切り口にする方針の第 1 段 (代表 2026-08-24
 *   壁打ち)。毎日の動線 (チェック + メニュー参照) を 1 タップにする。おんがくタブも同じ
 *   「領域ホーム」の型で作る予定なので、route から独立させておく。
 * @failure: 描画のみ。お子さん未選択はチェックを出さず案内文、資料が無ければ導線ごと出さない。
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import type { StudyPage } from "../api/pages";
import type { StudyProgressLog, StudyProgressLogInput } from "../api/plans";
import type { StudySource } from "../api/sources";
import type { ProgressLogsState } from "../hooks/useProgressLogs";
import { useSourcePageMap } from "../hooks/useSourcePageMap";
import { UNDOU_ASOBI_SOURCE_ID, UNDOU_MENU_SOURCE_ID } from "../model/motorItems";
import { MotorCheckCard } from "./MotorCheckCard";
import { LazyPageImage, SourcePageLightbox } from "./pageImages";
import "./study.css";

export function MotorPanel({
  familyId,
  favorites,
  onToggleFavorite,
  sourceHrefBase,
  planReady,
  hasLearners,
  sources,
  logsState,
  onAdd,
  onRemove,
  busy,
  actionError,
}: {
  familyId: string;
  /** お気に入りの遊び id (plan の draft。保存バーで確定する)。 */
  favorites: readonly string[];
  onToggleFavorite: (itemId: string) => void;
  sourceHrefBase: string;
  planReady: boolean;
  /** 家族にお子さんがいるか (いなければ案内文も出さない)。 */
  hasLearners: boolean;
  /** 本だなの資料 (無ければ null 扱いで導線を出さない)。 */
  sources: readonly StudySource[] | null;
  logsState: ProgressLogsState;
  onAdd: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onRemove: (id: string) => Promise<void>;
  busy: boolean;
  actionError: string | null;
}) {
  if (!planReady) {
    return hasLearners ? (
      <p className="study-muted">お子さんを選ぶとうんどうチェックが出ます。</p>
    ) : null;
  }
  const hasAsobiBook = sources?.some((s) => s.id === UNDOU_ASOBI_SOURCE_ID) === true;
  const hasMenu = sources?.some((s) => s.id === UNDOU_MENU_SOURCE_ID) === true;
  const menuHref = (query = "") =>
    `${sourceHrefBase}/${encodeURIComponent(UNDOU_MENU_SOURCE_ID)}${query}`;

  return (
    <>
      <section className="study-section">
        <h2 className="study-section-title">
          <span aria-hidden="true">🤸</span> うんどうチェック
        </h2>
        <p className="study-section-help">
          『0〜5歳児の…運動あそび』の遊びごとに ◎○△× で残します。同じ記号をもう一度押すと取り消し、p.○○ で本のページが開きます。
        </p>
        <MotorCheckCard
          familyId={familyId}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          logsState={logsState}
          onAdd={onAdd}
          onRemove={onRemove}
          busy={busy}
          actionError={actionError}
          {...(hasAsobiBook
            ? {
                pageHref: (page: number) =>
                  `${sourceHrefBase}/${UNDOU_ASOBI_SOURCE_ID}?page=${page}`,
              }
            : {})}
        />
      </section>

      {hasMenu ? <MenuPagesSection familyId={familyId} menuHref={menuHref} /> : null}
    </>
  );
}

/** 週メニューの 4 ページをページ内に埋め込む (遅延読み、タップで全画面)。 */
function MenuPagesSection({
  familyId,
  menuHref,
}: {
  familyId: string;
  menuHref: (query?: string) => string;
}) {
  const pageMap = useSourcePageMap(familyId, UNDOU_MENU_SOURCE_ID);
  const [enlarged, setEnlarged] = useState<{ page: StudyPage; label: string } | null>(null);
  const MENU_PAGES: { page: number; label: string }[] = [
    { page: 1, label: "週の型（A・B・C）と使い方" },
    { page: 2, label: "考査の種目 × 遊びの対照表" },
    { page: 3, label: "力別さくいん" },
    { page: 4, label: "2歳の親子あそびと裏づけ" },
  ];
  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🗓️</span> 週のメニュー
      </h2>
      <p className="study-section-help">
        A（かけっこと跳ぶ日）/ B（ぶら下がりとマットの日）/ C（ボールとリズムの日）を気分で回します。タップで拡大、
        <Link to={menuHref()}>本だなで開く</Link>こともできます。
      </p>
      <div className="study-menu-pages">
        {MENU_PAGES.map(({ page, label }) => {
          const p = pageMap?.[page];
          if (!p) return null;
          return (
            <figure key={page} className="study-menu-page">
              <LazyPageImage
                page={p}
                label={label}
                className="study-menu-page-img"
                onOpen={() => setEnlarged({ page: p, label })}
              />
              <figcaption>{label}</figcaption>
            </figure>
          );
        })}
      </div>
      {enlarged ? (
        <SourcePageLightbox
          page={enlarged.page}
          label={enlarged.label}
          onClose={() => setEnlarged(null)}
        />
      ) : null}
    </section>
  );
}
